export const KEYWORD_USAGE_SCHEMA_VERSION = 'listing_keyword_usage.v1';

const POLICY_LABELS = {
  must_use: '必须写入',
  can_use: '可写入',
  backend_only: '后台词',
  ads_only: '广告测试',
  forbidden: '禁用',
  selected: '已选词',
  unclassified: '未分级',
};

const SEMANTIC_ROLE_LABELS = {
  current_theme: '当前主题',
  physical_core: '商品核心',
  shared_intent: '共享搜索意图',
  adjacent_search: '相邻需求',
  unverified_traffic: '待验证流量词',
  blocked: '禁用词',
};

const SOURCE_LABELS = {
  sif: '卖家精灵 / SIF',
  sellersprite: '卖家精灵',
  seller_sprite: '卖家精灵',
  'seller-sprite': '卖家精灵',
  mcp: '市场工具',
  seller_sprite_mirror: '卖家精灵镜像',
  'seller-sprite-mirror': '卖家精灵镜像',
  traffic: '流量词库',
  mcp_expan: '市场扩展',
  market_expansion: '市场扩展',
  marketexpansion: '市场扩展',
  title: '竞品标题',
  bullet: '竞品五点',
  bullets: '竞品五点',
  description: '竞品描述',
  review: '竞品评论',
  manual: '人工策略',
  step02: 'Step02 选词',
  current_selection: '当前选词状态',
  search_team: 'Search Team 接纳',
  generation_ledger: '生成记录',
  'restored-search-terms': '历史 Search Terms',
};

const CATEGORY_LABELS = {
  coreKeywords: '核心词',
  longTailKeywords: '长尾词',
  painPointKeywords: '痛点词',
  sceneKeywords: '场景词',
  competitorKeywords: '竞品流量词',
  extendedKeywords: '扩展词',
  conversionKeywords: '转化词',
};

const PLAN_FIELD_LABELS = {
  title: '标题候选',
  bullets: '五点候选',
  description: '描述候选',
  searchTerms: 'Search Terms',
  theme: '主题词池',
  physical: '商品核心词池',
  keywordPool: '可用词池',
};

const VISIBLE_TEXT_KEYS = new Set([
  'title',
  'headline',
  'heading',
  'subheading',
  'label',
  'value',
  'text',
  'textEn',
  'body',
  'bodyText',
  'content',
  'description',
  'caption',
  'altText',
  'bullet',
  'bullets',
  'question',
  'questionEn',
  'answer',
  'answerEn',
]);

const VISIBLE_TEXT_CONTAINER_KEYS = new Set([
  'items',
  'rows',
  'columns',
  'cards',
  'features',
  'comparisonItems',
  'specifications',
  'contentBlocks',
]);

const LOW_VALUE_SEARCH_TERM_TOKENS = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'the', 'to', 'with',
  'de', 'du', 'des', 'd', 'en', 'et', 'ou', 'pour', 'avec', 'sur', 'dans', 'un', 'une', 'le', 'la', 'les',
  'der', 'die', 'das', 'dem', 'den', 'ein', 'eine', 'einer', 'einem', 'einen', 'und', 'oder', 'mit', 'ohne',
  'fur', 'fuer', 'zum', 'zur', 'von', 'vom', 'im', 'am', 'ist', 'sind', 'wird', 'werden', 'dein', 'deine',
  'ce', 'cet', 'cette', 'ces', 'son', 'sa', 'ses', 'est', 'sont', 'aux', 'au',
  'il', 'lo', 'gli', 'uno', 'una', 'del', 'della', 'delle', 'dei', 'nel', 'nella', 'alla',
  'el', 'los', 'las', 'unos', 'unas', 'para', 'por', 'con',
]);

const POLICY_PRIORITY = {
  forbidden: 6,
  ads_only: 5,
  must_use: 4,
  backend_only: 3,
  can_use: 2,
  selected: 1,
  unclassified: 0,
};

function asArray(value) {
  if (value instanceof Set) return [...value];
  return Array.isArray(value) ? value : [];
}

function cleanText(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function normalizeComparable(value) {
  return cleanText(value)
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isCjkText(value) {
  return /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/u.test(value);
}

function containsNormalizedKeyword(normalizedText, normalizedKeyword) {
  if (!normalizedText || !normalizedKeyword) return false;
  if (isCjkText(normalizedKeyword)) {
    return normalizedText.replace(/\s+/g, '').includes(normalizedKeyword.replace(/\s+/g, ''));
  }
  return ` ${normalizedText} `.includes(` ${normalizedKeyword} `);
}

export function containsExactKeyword(text, keyword) {
  const normalizedText = normalizeComparable(text);
  const normalizedKeyword = normalizeComparable(keyword);
  return containsNormalizedKeyword(normalizedText, normalizedKeyword);
}

function finiteMarketMetric(value, { allowZero = false } = {}) {
  if (value === '' || value === undefined || value === null) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number > 0 || (allowZero && number === 0) ? number : null;
}

function hasMetricEvidence(entry, key) {
  if (!entry || typeof entry !== 'object') return false;
  const containers = [
    entry.metricAvailability,
    entry.metricPresence,
    entry.metricSources,
    entry.metrics?.observedFields,
    entry.evidence?.metricAvailability,
    entry.evidence?.metricPresence,
    entry.evidence?.metricSources,
  ];
  return containers.some((container) => {
    if (Array.isArray(container)) return container.includes(key);
    if (!container || typeof container !== 'object') return false;
    const value = container[key];
    return value === true || (typeof value === 'string' && value.trim().length > 0);
  });
}

function metricSourceRank(entry, keys) {
  const explicitlyObserved = keys.some((key) => hasMetricEvidence(entry, key));
  if (explicitlyObserved) return 4;
  const marketSourced = [
    entry?.source,
    ...asArray(entry?.sources),
  ].map((source) => (
    source && typeof source === 'object'
      ? cleanText(source.tool || source.provider || source.stage || source.role)
      : cleanText(source)
  ).replace(/[_-]+/g, ' ')).some((source) => (
    /(?:^|\s)(sif|seller\s*sprite|sellersprite|mcp|market|traffic|conversion|extended)(?:$|\s)/i.test(source)
  ));
  return marketSourced ? 3 : 1;
}

function keywordText(entry) {
  if (typeof entry === 'string' || typeof entry === 'number') return cleanText(entry);
  if (!entry || typeof entry !== 'object') return '';
  return cleanText(
    entry.keyword
    || entry.term
    || entry.word
    || entry.text
    || entry.normalizedKeyword,
  );
}

function addStringValues(target, values) {
  for (const value of asArray(values)) {
    const text = cleanText(value);
    if (text) target.add(text);
  }
}

function metricObservation(entry, keys) {
  if (!entry || typeof entry !== 'object') return null;
  const allowDirectZero = keys.some((key) => hasMetricEvidence(entry, key));
  const allowEvidenceZero = keys.some((key) => hasMetricEvidence(entry.evidence, key));
  for (const key of keys) {
    const direct = finiteMarketMetric(entry[key], { allowZero: allowDirectZero });
    if (direct !== null) {
      return { value: direct, rank: metricSourceRank(entry, keys) };
    }
    const evidence = finiteMarketMetric(entry.evidence?.[key], { allowZero: allowEvidenceZero });
    if (evidence !== null) {
      return { value: evidence, rank: metricSourceRank(entry.evidence, keys) };
    }
    const nested = finiteMarketMetric(entry.metrics?.[key], {
      allowZero: asArray(entry.metrics?.observedFields).includes(key),
    });
    if (nested !== null) {
      return { value: nested, rank: metricSourceRank(entry, keys) };
    }
  }
  return null;
}

function mergeMetric(candidate, metricKey, entry, aliases) {
  const observation = metricObservation(entry, aliases);
  if (!observation) return;
  if (observation.rank < candidate.metricRanks[metricKey]) return;
  candidate.metrics[metricKey] = observation.value;
  candidate.metricRanks[metricKey] = observation.rank;
}

function sourceValues(entry) {
  if (!entry || typeof entry !== 'object') return [];
  return [
    entry.source,
    ...(asArray(entry.sources)),
    ...(asArray(entry.evidence?.sources)),
  ].map((source) => {
    if (!source || typeof source !== 'object') return cleanText(source);
    return cleanText(source.tool || source.provider || source.stage || source.role);
  }).filter(Boolean);
}

function sourceAsinValues(entry) {
  if (!entry || typeof entry !== 'object') return [];
  return [
    entry.sourceAsin,
    ...(asArray(entry.sourceAsins)),
    ...(asArray(entry.evidence?.sourceAsins)),
    ...asArray(entry.sources).map((source) => source?.asin),
  ].map(cleanText).filter(Boolean);
}

function createCandidate(keyword) {
  return {
    keyword,
    normalizedKeyword: normalizeComparable(keyword),
    policy: 'unclassified',
    policyOriginRank: 0,
    semanticRole: '',
    reason: '',
    metrics: {
      searches30d: null,
      purchaseRate: null,
      competition: null,
      cpc: null,
      relevanceScore: null,
      supplyDemandRatio: null,
      frequency: null,
      policyScore: null,
    },
    metricRanks: {
      searches30d: -1,
      purchaseRate: -1,
      competition: -1,
      cpc: -1,
      relevanceScore: -1,
      supplyDemandRatio: -1,
      frequency: -1,
    },
    sources: new Set(),
    sourceAsins: new Set(),
    categories: new Set(),
    plannedFields: new Set(),
    preferredFields: new Set(),
    blockedFields: new Set(),
    originKinds: new Set(),
    selectionEvidence: new Set(),
    selectedForSearchTerms: false,
    backendAuthority: false,
    marketExpansion: false,
  };
}

function mergePolicy(candidate, policy, originRank) {
  const aliases = {
    visible: 'can_use',
    context_only: 'ads_only',
    blocked: 'forbidden',
  };
  const rawPolicy = cleanText(policy).toLocaleLowerCase();
  const normalizedPolicy = aliases[rawPolicy] || rawPolicy;
  if (!normalizedPolicy) return false;
  const currentPriority = POLICY_PRIORITY[candidate.policy] ?? -1;
  const nextPriority = POLICY_PRIORITY[normalizedPolicy] ?? -1;
  if (
    originRank > candidate.policyOriginRank
    || (originRank === candidate.policyOriginRank && nextPriority > currentPriority)
  ) {
    candidate.policy = normalizedPolicy;
    candidate.policyOriginRank = originRank;
    return true;
  }
  return false;
}

function replacePolicyDetails(candidate, entry = {}) {
  candidate.semanticRole = cleanText(entry.semanticRole || entry.semantic_role);
  candidate.reason = cleanText(entry.reason || entry.rationale).slice(0, 500);
  candidate.preferredFields.clear();
  candidate.blockedFields.clear();
  addStringValues(candidate.preferredFields, entry.preferredFields);
  addStringValues(candidate.blockedFields, entry.blockedFields);
  const policyScore = entry.score ?? entry.policyScore;
  candidate.metrics.policyScore = finiteMarketMetric(policyScore, { allowZero: true });
}

function mergeCandidate(store, entry, meta = {}) {
  const keyword = keywordText(entry);
  const normalizedKeyword = normalizeComparable(keyword);
  if (!normalizedKeyword) return null;

  let candidate = store.get(normalizedKeyword);
  if (!candidate) {
    candidate = createCandidate(keyword);
    store.set(normalizedKeyword, candidate);
  }

  const originKind = cleanText(meta.originKind);
  if (originKind) candidate.originKinds.add(originKind);
  const selectionEvidence = cleanText(meta.selectionEvidence);
  if (selectionEvidence) candidate.selectionEvidence.add(selectionEvidence);
  if (meta.category) candidate.categories.add(meta.category);
  if (meta.plannedField) candidate.plannedFields.add(meta.plannedField);
  if (meta.selectedForSearchTerms) candidate.selectedForSearchTerms = true;
  if (meta.backendAuthority) candidate.backendAuthority = true;
  if (meta.marketExpansion) candidate.marketExpansion = true;

  if (entry && typeof entry === 'object') {
    const policy = meta.policy || entry.policy;
    const policyReplaced = mergePolicy(
      candidate,
      policy,
      meta.policyOriginRank ?? (policy ? 2 : 0),
    );
    if (policyReplaced) {
      replacePolicyDetails(candidate, entry);
    } else if (!policy && candidate.policyOriginRank === 0) {
      if (!candidate.semanticRole) {
        candidate.semanticRole = cleanText(entry.semanticRole || entry.semantic_role);
      }
      if (!candidate.reason) {
        candidate.reason = cleanText(entry.reason || entry.rationale).slice(0, 500);
      }
      addStringValues(candidate.preferredFields, entry.preferredFields);
      addStringValues(candidate.blockedFields, entry.blockedFields);
    }

    mergeMetric(candidate, 'searches30d', entry, ['searches30d', 'searches', 'volume', '30天搜索量']);
    mergeMetric(candidate, 'purchaseRate', entry, ['purchaseRate', 'purchase_rate', 'conversionRate']);
    mergeMetric(candidate, 'competition', entry, ['competition', 'products', 'productCount']);
    mergeMetric(candidate, 'cpc', entry, ['cpc', 'bid', 'suggestedBid']);
    mergeMetric(candidate, 'relevanceScore', entry, ['relevanceScore', 'relevance_score']);
    mergeMetric(candidate, 'supplyDemandRatio', entry, ['supplyDemandRatio', 'supply_demand_ratio']);
    mergeMetric(candidate, 'frequency', entry, ['frequency', 'rankFrequency']);

    addStringValues(candidate.sources, sourceValues(entry));
    addStringValues(candidate.sourceAsins, sourceAsinValues(entry));
  } else if (meta.policy) {
    const policyReplaced = mergePolicy(candidate, meta.policy, meta.policyOriginRank ?? 1);
    if (policyReplaced) replacePolicyDetails(candidate);
  }

  if (meta.source) candidate.sources.add(meta.source);
  return candidate;
}

function addKeywordLibrary(store, keywordLibrary) {
  for (const entry of asArray(keywordLibrary?.keywords)) {
    mergeCandidate(store, entry, { originKind: 'keywordLibrary' });
  }
  for (const [category, entries] of Object.entries(keywordLibrary?.categories || {})) {
    for (const entry of asArray(entries)) {
      mergeCandidate(store, entry, { originKind: 'keywordLibrary', category });
    }
  }
}

function assetGroupMeta(groupName) {
  const mapping = {
    title: { plannedField: 'title' },
    bullets: { plannedField: 'bullets' },
    description: { plannedField: 'description' },
    backendOnly: { plannedField: 'searchTerms', backendAuthority: true, policy: 'backend_only' },
    selectedKeywords: {
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: 'accepted_search_terms',
      policy: 'selected',
    },
    selected: {
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: 'accepted_search_terms',
      policy: 'selected',
    },
    theme: { plannedField: 'theme' },
    physical: { plannedField: 'physical' },
    blocked: { policy: 'forbidden' },
    contextOnly: { policy: 'ads_only' },
  };
  return mapping[groupName] || {};
}

function addAssetPackages(store, packages) {
  for (const assetPackage of asArray(packages)) {
    for (const [groupName, entries] of Object.entries(assetPackage?.groups || {})) {
      const groupMeta = assetGroupMeta(groupName);
      for (const entry of asArray(entries)) {
        mergeCandidate(store, entry, {
          ...groupMeta,
          originKind: 'keywordAssetPackage',
          policyOriginRank: entry?.policy ? 2 : 1,
        });
      }
    }
  }
}

function addPolicies(store, policies) {
  for (const entry of asArray(policies)) {
    mergeCandidate(store, entry, {
      originKind: 'keywordUsePolicy',
      policyOriginRank: 3,
      backendAuthority: cleanText(entry?.policy).toLocaleLowerCase() === 'backend_only',
    });
  }
}

function addKeywordPlan(store, keywordPlan) {
  const fields = {
    keywordPool: { plannedField: 'keywordPool' },
    titleKeywords: { plannedField: 'title' },
    bulletKeywords: { plannedField: 'bullets' },
    descriptionKeywords: { plannedField: 'description' },
    themeKeywords: { plannedField: 'theme' },
    physicalKeywords: { plannedField: 'physical' },
    selectedKeywords: {
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: 'planned_search_terms',
      policy: 'selected',
    },
    lockedKeywords: {
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: 'planned_search_terms',
      policy: 'selected',
    },
    backendOnly: { plannedField: 'searchTerms', backendAuthority: true, policy: 'backend_only' },
    blockedKeywords: { policy: 'forbidden' },
  };
  for (const [key, meta] of Object.entries(fields)) {
    for (const entry of asArray(keywordPlan?.[key])) {
      mergeCandidate(store, entry, {
        ...meta,
        originKind: 'keywordPlan',
        policyOriginRank: meta.policy ? 1 : 0,
      });
    }
  }
}

function searchStageSelection(generationLedger) {
  const stage = asArray(generationLedger?.stages)
    .find((item) => item?.stage === 'search_team');
  return asArray(stage?.acceptedOutput?.selected);
}

function addKeywordBrief(store, keywordBrief) {
  const decisions = keywordBrief?.decisions || {};
  const titleIds = new Set(asArray(decisions.titleIds));
  const bodyIds = new Set(asArray(decisions.bodyIds));
  const backendIds = new Set(asArray(decisions.backendReserveIds));
  const rationaleById = decisions.rationaleById || {};
  const semanticRoleByLane = {
    physical: 'physical_core',
    theme: 'current_theme',
    shared: 'shared_intent',
    supplemental: 'adjacent_search',
  };
  for (const entry of asArray(keywordBrief?.candidates)) {
    const candidate = mergeCandidate(store, {
      ...entry,
      semanticRole: semanticRoleByLane[entry?.lane] || '',
      reason: rationaleById[entry?.id] || '',
    }, {
      originKind: 'generationLedger',
      policyOriginRank: 2,
      backendAuthority: backendIds.has(entry?.id),
      marketExpansion: asArray(entry?.sources).some((source) => (
        cleanText(source?.stage).toLocaleLowerCase() === 'step03_market_expansion'
      )),
    });
    if (!candidate) continue;
    if (titleIds.has(entry?.id)) candidate.plannedFields.add('title');
    if (bodyIds.has(entry?.id)) candidate.plannedFields.add('bullets');
    if (backendIds.has(entry?.id)) candidate.plannedFields.add('searchTerms');
  }
}

function addGenerationLedger(store, generationLedger) {
  addKeywordBrief(store, generationLedger?.keywordBrief);
  const groups = [
    [generationLedger?.step02?.keywordPool, { plannedField: 'keywordPool' }],
    [generationLedger?.step02?.selectedKeywords, {
      plannedField: 'searchTerms',
      selectedForSearchTerms: true,
      backendAuthority: true,
      selectionEvidence: 'step02',
      source: 'step02',
      policy: 'selected',
    }],
    [generationLedger?.step03KeywordPool, { plannedField: 'keywordPool' }],
    [generationLedger?.marketExpansion?.candidates, {
      plannedField: 'keywordPool',
      marketExpansion: true,
      source: 'market_expansion',
    }],
    [generationLedger?.marketExpansion?.candidateRecords, {
      plannedField: 'keywordPool',
      marketExpansion: true,
      source: 'market_expansion',
    }],
    [searchStageSelection(generationLedger), {
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: 'accepted_search_terms',
      source: 'generation_ledger',
      policy: 'backend_only',
    }],
  ];
  for (const [entries, meta] of groups) {
    for (const entry of asArray(entries)) {
      mergeCandidate(store, entry, {
        ...meta,
        originKind: 'generationLedger',
        policyOriginRank: meta.policy ? 1 : 0,
      });
    }
  }
}

function addSearchTermsCandidates(store, {
  listing,
  listingDraftV1,
  searchTermsSelectionLedger,
  selectedKeywords,
  keywordPlan,
  generationLedger,
} = {}) {
  const searchTerms = listing?.searchTerms || {};
  const finalizerOwned = searchTerms?.source === 'listing_v4_code_finalizer';
  const restoredDraftOwnedByFinalizer = listingDraftV1?.searchTerms?.source === 'listing_v4_code_finalizer';
  const ledgerSource = cleanText(searchTermsSelectionLedger?.source).toLocaleLowerCase();
  const authorityGroupsByPriority = [
    {
      entries: searchTermsSelectionLedger?.selected,
      selectionEvidence: ledgerSource.includes('step02') ? 'step02' : 'accepted_search_terms',
      source: ledgerSource || 'generation_ledger',
    },
    {
      entries: searchStageSelection(generationLedger),
      selectionEvidence: 'accepted_search_terms',
      source: 'generation_ledger',
    },
    {
      entries: searchTerms?.approvedKeywords,
      selectionEvidence: 'accepted_search_terms',
      source: finalizerOwned ? 'generation_ledger' : 'current_selection',
    },
    {
      entries: listingDraftV1?.searchTerms?.selected,
      selectionEvidence: restoredDraftOwnedByFinalizer ? 'accepted_search_terms' : 'restored_search_terms',
      source: restoredDraftOwnedByFinalizer ? 'generation_ledger' : 'restored-search-terms',
    },
    {
      entries: finalizerOwned ? searchTerms?.candidates : [],
      selectionEvidence: 'accepted_search_terms',
      source: 'generation_ledger',
    },
    {
      entries: finalizerOwned ? searchTerms?.selected : [],
      selectionEvidence: 'accepted_search_terms',
      source: 'generation_ledger',
    },
    {
      entries: selectedKeywords,
      selectionEvidence: 'current_state',
      source: 'current_selection',
    },
    {
      entries: keywordPlan?.selectedKeywords,
      selectionEvidence: 'planned_search_terms',
      source: 'generation_ledger',
    },
    {
      entries: searchTerms?.selected,
      selectionEvidence: finalizerOwned ? 'accepted_search_terms' : 'restored_search_terms',
      source: finalizerOwned ? 'generation_ledger' : 'restored-search-terms',
    },
    {
      entries: searchTerms?.candidates,
      selectionEvidence: finalizerOwned ? 'accepted_search_terms' : 'restored_search_terms',
      source: finalizerOwned ? 'generation_ledger' : 'restored-search-terms',
    },
  ];
  const viableAuthorityGroups = authorityGroupsByPriority
    .map((group) => ({
      ...group,
      entries: asArray(group.entries).filter((entry) => keywordText(entry)),
    }))
    .filter((group) => group.entries.length);
  const visibleText = visibleFieldEntries(listing).map((entry) => entry.text).join(' ');
  const backendText = searchTermsText(listing);
  const currentTokenSet = normalizedTokenSet(`${visibleText} ${backendText}`);
  const entryIsCurrentlyCovered = (entry) => {
    const keyword = keywordText(entry);
    if (containsExactKeyword(visibleText, keyword) || containsExactKeyword(backendText, keyword)) {
      return true;
    }
    const tokens = meaningfulKeywordTokens(keyword);
    return tokens.length > 0 && tokens.every((token) => currentTokenSet.has(token));
  };
  const authoritativeEntries = [];
  const authoritativeKeys = new Set();
  for (const group of viableAuthorityGroups) {
    for (const entry of group.entries) {
      if (!entryIsCurrentlyCovered(entry)) continue;
      const keyword = keywordText(entry);
      const key = normalizeComparable(keyword);
      if (!key || authoritativeKeys.has(key)) continue;
      const tokens = meaningfulKeywordTokens(keyword);
      const tokenSignature = keywordTokenSignature(keyword);
      const equivalentIndex = tokenSignature
        ? authoritativeEntries.findIndex((existing) => (
            keywordTokenSignature(keywordText(existing.entry)) === tokenSignature
          ))
        : -1;
      if (equivalentIndex >= 0) {
        const existing = authoritativeEntries[equivalentIndex];
        const existingKeyword = keywordText(existing.entry);
        const currentExact = containsExactKeyword(visibleText, keyword)
          || containsExactKeyword(backendText, keyword);
        const existingExact = containsExactKeyword(visibleText, existingKeyword)
          || containsExactKeyword(backendText, existingKeyword);
        if (!currentExact || existingExact) continue;
        authoritativeKeys.delete(normalizeComparable(existingKeyword));
        authoritativeEntries.splice(equivalentIndex, 1);
      }
      if (tokens.length === 1) {
        const tokenAlreadyRepresented = authoritativeEntries.some((existing) => (
          meaningfulKeywordTokens(keywordText(existing.entry)).length > 1
          && meaningfulKeywordTokens(keywordText(existing.entry)).includes(tokens[0])
        ));
        if (tokenAlreadyRepresented) continue;
      } else {
        for (let index = authoritativeEntries.length - 1; index >= 0; index -= 1) {
          const existingTokens = meaningfulKeywordTokens(keywordText(authoritativeEntries[index].entry));
          if (existingTokens.length === 1 && tokens.includes(existingTokens[0])) {
            authoritativeKeys.delete(normalizeComparable(keywordText(authoritativeEntries[index].entry)));
            authoritativeEntries.splice(index, 1);
          }
        }
      }
      authoritativeEntries.push({ entry, group });
      authoritativeKeys.add(key);
    }
  }
  if (!authoritativeEntries.length && viableAuthorityGroups.length) {
    authoritativeEntries.push(...viableAuthorityGroups[0].entries.map((entry) => ({
      entry,
      group: viableAuthorityGroups[0],
    })));
  }
  let authorityCount = 0;
  for (const { entry, group } of authoritativeEntries) {
    authorityCount += 1;
    mergeCandidate(store, entry, {
      originKind: 'searchTermsAuthority',
      plannedField: 'searchTerms',
      backendAuthority: true,
      selectedForSearchTerms: true,
      selectionEvidence: group.selectionEvidence,
      policy: 'selected',
      policyOriginRank: 1,
      source: group.source,
    });
  }

  // `normalizeSearchTermsDraft` may expose the final backend text as individual
  // token entries. Once phrase-level selections are available, those tokens are
  // implementation detail rather than separately selected business keywords.
  if (authorityCount === 0) {
    for (const entry of asArray(searchTerms?.keywords)) {
      mergeCandidate(store, entry, {
        originKind: 'searchTerms',
        plannedField: 'searchTerms',
        backendAuthority: true,
        selectionEvidence: finalizerOwned ? 'accepted_search_terms' : 'restored_search_terms',
        source: entry?.source || (finalizerOwned ? 'generation_ledger' : 'restored-search-terms'),
      });
    }
  }

  if (authorityCount === 0) {
    for (const entry of [
      ...asArray(searchTerms?.candidates),
      ...asArray(searchTerms?.selected),
    ]) {
      mergeCandidate(store, entry, {
        originKind: 'searchTerms',
        plannedField: 'searchTerms',
        backendAuthority: true,
        selectionEvidence: finalizerOwned ? 'accepted_search_terms' : 'restored_search_terms',
        policy: 'selected',
        policyOriginRank: 1,
        source: finalizerOwned ? 'generation_ledger' : 'restored-search-terms',
      });
    }
  }

  if (authorityCount === 0 && store.size === 0) {
    for (const token of normalizeComparable(searchTerms?.searchTermsEn || listing?.searchTermsEn).split(' ')) {
      if (!token) continue;
      mergeCandidate(store, token, {
        originKind: 'searchTermsFallback',
        plannedField: 'searchTerms',
        backendAuthority: true,
        selectionEvidence: 'restored_search_terms',
        policy: 'selected',
        policyOriginRank: 1,
        source: 'restored-search-terms',
      });
    }
  }
}

function addOptimizerResult(store, optimizerResult) {
  for (const entry of asArray(optimizerResult?.coveredCore)) {
    mergeCandidate(store, entry, {
      originKind: 'optimizerResult',
      plannedField: 'keywordPool',
      source: 'generation_ledger',
    });
  }
}

function collectVisibleStrings(value, depth = 0, seen = new WeakSet(), allowPrimitive = false) {
  if (depth > 5 || value === null || value === undefined) return [];
  if (typeof value === 'string' || typeof value === 'number') {
    return allowPrimitive ? [cleanText(value)].filter(Boolean) : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectVisibleStrings(item, depth + 1, seen, allowPrimitive));
  }
  if (typeof value !== 'object' || seen.has(value)) return [];
  seen.add(value);
  const output = [];
  for (const [key, nested] of Object.entries(value)) {
    if (VISIBLE_TEXT_KEYS.has(key)) {
      output.push(...collectVisibleStrings(nested, depth + 1, seen, true));
      continue;
    }
    if (VISIBLE_TEXT_CONTAINER_KEYS.has(key) && nested && typeof nested === 'object') {
      output.push(...collectVisibleStrings(nested, depth + 1, seen, false));
    }
  }
  return output;
}

function qaText(item) {
  return [
    item?.questionEn,
    item?.answerEn,
    item?.question,
    item?.answer,
  ].map(cleanText).filter(Boolean).join(' ');
}

function preferredListingField(listing, canonicalKey, aliases = []) {
  if (Object.prototype.hasOwnProperty.call(listing || {}, canonicalKey)) {
    return listing?.[canonicalKey];
  }
  for (const alias of aliases) {
    const value = listing?.[alias];
    if (cleanText(value)) return value;
  }
  return '';
}

function visibleFieldEntries(listing) {
  const entries = [
    {
      field: 'productTitle',
      label: 'Product Title',
      order: 0,
      text: preferredListingField(listing, 'productTitle', ['titleEn', 'titleMain']),
    },
    {
      field: 'itemHighlights',
      label: 'Item Highlights',
      order: 1,
      text: preferredListingField(listing, 'itemHighlights', ['titleSub']),
    },
  ];
  for (const [index, bullet] of asArray(listing?.bulletsEn).entries()) {
    entries.push({
      field: 'bullet',
      index,
      label: `五点 ${index + 1}`,
      order: 10 + index,
      text: bullet,
    });
  }
  entries.push({
    field: 'description',
    label: '商品描述',
    order: 30,
    text: listing?.descriptionEn,
  });
  for (const [index, item] of asArray(listing?.qa).entries()) {
    entries.push({
      field: 'qa',
      index,
      label: `Q&A ${index + 1}`,
      order: 40 + index,
      text: qaText(item),
    });
  }
  for (const [index, module] of asArray(listing?.aplusModules).entries()) {
    entries.push({
      field: 'aplus',
      index,
      label: `A+ ${index + 1}`,
      order: 60 + index,
      text: collectVisibleStrings(module).join(' '),
    });
  }
  return entries;
}

function searchTermsText(listing) {
  return cleanText(
    listing?.searchTerms?.searchTermsEn
    || listing?.searchTermsEn
    || listing?.searchTerms?.text,
  );
}

function meaningfulKeywordTokens(keyword) {
  return [...new Set(normalizeComparable(keyword).split(' ').filter((token) => (
    token && !LOW_VALUE_SEARCH_TERM_TOKENS.has(token)
  )))];
}

function keywordTokenSignature(keyword) {
  const tokens = meaningfulKeywordTokens(keyword);
  return tokens.length > 1 ? [...tokens].sort().join('\u0000') : '';
}

function normalizedTokenSet(value) {
  return new Set(normalizeComparable(value).split(' ').filter(Boolean));
}

function buildPlacementContext(listing) {
  const visibleEntries = visibleFieldEntries(listing).map((entry) => ({
    ...entry,
    normalizedText: normalizeComparable(entry.text),
    tokenSet: normalizedTokenSet(entry.text),
  }));
  const backendText = searchTermsText(listing);
  return {
    visibleEntries,
    backendNormalized: normalizeComparable(backendText),
    backendTokens: normalizedTokenSet(backendText),
  };
}

function buildPlacements(candidate, context) {
  const {
    visibleEntries,
    backendNormalized,
    backendTokens,
  } = context;
  const placements = visibleEntries
    .filter((entry) => containsNormalizedKeyword(entry.normalizedText, candidate.normalizedKeyword))
    .map(({ field, index, label, order }) => ({
      field,
      ...(Number.isInteger(index) ? { index } : {}),
      label,
      order,
      matchType: 'exact_phrase',
      matchedTokens: [],
    }));

  const keywordNormalized = candidate.normalizedKeyword;
  if (backendNormalized && keywordNormalized) {
    const keywordTokens = [...new Set(keywordNormalized.split(' ').filter(Boolean))];
    const matchedTokens = keywordTokens.filter((token) => backendTokens.has(token));
    const tokenizedMatch = candidate.backendAuthority
      && keywordTokens.length > 0
      && matchedTokens.length === keywordTokens.length;
    const exactMatch = containsNormalizedKeyword(backendNormalized, keywordNormalized);
    if (tokenizedMatch || exactMatch) {
      placements.push({
        field: 'searchTerms',
        label: 'Search Terms',
        order: 90,
        matchType: exactMatch ? 'exact_phrase' : 'backend_tokenized',
        matchedTokens,
      });
    }
  }
  if (placements.length || (!candidate.backendAuthority && !candidate.selectedForSearchTerms)) {
    return placements;
  }

  const requiredTokens = meaningfulKeywordTokens(candidate.keyword);
  if (!requiredTokens.length) return placements;
  const tokenCoverage = new Set();
  const partialVisiblePlacements = [];
  for (const entry of visibleEntries) {
    const matchedTokens = requiredTokens.filter((token) => entry.tokenSet.has(token));
    if (!matchedTokens.length) continue;
    matchedTokens.forEach((token) => tokenCoverage.add(token));
    partialVisiblePlacements.push({
      field: entry.field,
      ...(Number.isInteger(entry.index) ? { index: entry.index } : {}),
      label: entry.label,
      order: entry.order,
      matchType: 'distributed_tokens',
      matchedTokens,
    });
  }
  const backendMatchedTokens = requiredTokens.filter((token) => backendTokens.has(token));
  backendMatchedTokens.forEach((token) => tokenCoverage.add(token));
  if (tokenCoverage.size !== requiredTokens.length) return placements;

  placements.push(...partialVisiblePlacements);
  if (backendMatchedTokens.length) {
    placements.push({
      field: 'searchTerms',
      label: 'Search Terms',
      order: 90,
      matchType: 'backend_tokenized',
      matchedTokens: backendMatchedTokens,
    });
  }
  return placements;
}

function normalizedFieldName(value) {
  return cleanText(value).toLocaleLowerCase().replace(/[\s_-]+/g, '');
}

function hasPolicyConflict(candidate, placements) {
  const policy = candidate.policy;
  if (policy === 'forbidden') return true;
  const blocked = new Set([...candidate.blockedFields].map(normalizedFieldName));
  if (policy === 'ads_only') {
    // PPC traffic terms may be admitted to backend Search Terms only after the
    // final Product Truth review. They are still never valid front-copy terms,
    // and an explicit Search Terms block remains a hard conflict.
    return placements.some((item) => item.field !== 'searchTerms')
      || (blocked.has('searchterms') && placements.some((item) => item.field === 'searchTerms'));
  }
  const visiblePlacements = placements.filter((item) => item.field !== 'searchTerms');
  const exactVisiblePlacements = visiblePlacements.filter((item) => item.matchType === 'exact_phrase');
  return exactVisiblePlacements.some((placement) => {
    const aliases = {
      productTitle: ['title', 'producttitle'],
      itemHighlights: ['title', 'itemhighlights'],
      bullet: ['bullets', 'bullet'],
      description: ['description'],
      qa: ['qa'],
      aplus: ['aplus'],
      searchTerms: ['searchterms'],
    }[placement.field] || [placement.field];
    return aliases.some((alias) => blocked.has(alias));
  });
}

function whyText(candidate, placements, conflict) {
  if (conflict) {
    return candidate.reason
      ? `${candidate.reason} 当前实际使用位置与该策略边界冲突，请结合诊断结果处理。`
      : '该词当前实际出现，但与既定关键词准入或字段边界冲突，请结合诊断结果处理。';
  }
  if (candidate.reason) return candidate.reason;
  if (candidate.marketExpansion) {
    return '原可用词不足后由市场工具补充，并进入生成候选池；当前 Listing 中已实际使用。';
  }
  if (candidate.selectedForSearchTerms) {
    const hasBackendPlacement = placements.some((item) => item.field === 'searchTerms');
    const hasVisiblePlacement = placements.some((item) => item.field !== 'searchTerms');
    if (candidate.selectionEvidence.has('step02')) {
      if (hasBackendPlacement && hasVisiblePlacement) {
        return 'Step02 已选中该关键词；系统去重后，它的有效词元分布在前台文案与 Search Terms 中。';
      }
      if (hasBackendPlacement) {
        return 'Step02 已将该词选入 Search Terms，当前后台搜索词中仍实际保留。';
      }
      return 'Step02 曾将该词选入 Search Terms；其有效词元已由当前前台文案覆盖，因此后台无需重复。';
    }
    if (candidate.selectionEvidence.has('accepted_search_terms')) {
      if (hasBackendPlacement && hasVisiblePlacement) {
        return '生成阶段已接纳该关键词用于 Search Terms；去重后，它的有效词元分布在前台文案与后台词中。';
      }
      if (hasBackendPlacement) {
        return '生成阶段已接纳该关键词用于 Search Terms，当前后台词中仍实际保留。';
      }
      return '生成阶段曾接纳该关键词用于 Search Terms；当前前台文案已覆盖其有效词元。';
    }
    if (candidate.selectionEvidence.has('planned_search_terms')) {
      if (hasBackendPlacement && hasVisiblePlacement) {
        return '生成规划将该词纳入 Search Terms；当前由前台文案与后台词共同覆盖。';
      }
      if (hasBackendPlacement) {
        return '生成规划将该词纳入 Search Terms，当前后台词中仍实际保留。';
      }
      return '生成规划曾将该词纳入 Search Terms；当前前台文案已覆盖其有效词元。';
    }
    if (hasBackendPlacement && hasVisiblePlacement) {
      return '当前文案与 Search Terms 共同覆盖该词；历史记录未保存原始选词原因。';
    }
    if (hasBackendPlacement) {
      return '当前 Search Terms 中保留该词；历史记录未保存原始选词原因。';
    }
    return '当前前台文案覆盖该词；历史记录未保存原始选词原因。';
  }
  const planned = [...candidate.plannedFields]
    .map((field) => PLAN_FIELD_LABELS[field] || field)
    .filter(Boolean);
  if (planned.length) {
    return `生成策略将该词分配到${planned.join('、')}，当前 Listing 中已实际使用。`;
  }
  const categories = [...candidate.categories]
    .map((category) => CATEGORY_LABELS[category] || category)
    .filter(Boolean);
  if (categories.length) {
    return `词库将该词归入${categories.join('、')}；当前 Listing 中已实际使用。`;
  }
  if (placements.length) {
    return '历史生成记录未保存该词的准入原因；这里只确认它当前确实出现在 Listing 中。';
  }
  return '';
}

function unusedStatus(candidate) {
  if (candidate.policy === 'forbidden') return 'excluded';
  if (candidate.policy === 'ads_only') {
    return [...candidate.blockedFields].map(normalizedFieldName).includes('searchterms')
      ? 'excluded'
      : 'traffic_test_not_used';
  }
  if (candidate.policy === 'must_use') return 'missing_required';
  if (candidate.selectedForSearchTerms) return 'selected_not_used';
  if (candidate.plannedFields.size) return 'planned_not_used';
  return 'not_used';
}

function unusedStatusLabel(status) {
  return {
    excluded: '策略排除',
    missing_required: '应使用但缺失',
    selected_not_used: '已选但未落稿',
    planned_not_used: '规划后未采用',
    traffic_test_not_used: '广告候选未采用',
    not_used: '本版未采用',
  }[status] || '本版未采用';
}

function whyUnusedText(candidate) {
  if (candidate.policy === 'forbidden') {
    return candidate.reason
      ? `${candidate.reason} 因此不进入前台文案或 Search Terms。`
      : '关键词被策略明确禁用，不进入前台文案或 Search Terms。';
  }
  if (candidate.policy === 'ads_only') {
    const searchTermsBlocked = [...candidate.blockedFields]
      .map(normalizedFieldName)
      .includes('searchterms');
    if (!searchTermsBlocked) {
      return candidate.reason
        ? `${candidate.reason} 当前保留作广告流量候选；仅在最终事实复核通过后可进入 Search Terms，不自动写入前台。`
        : '该词当前保留作广告流量候选；仅在最终事实复核通过后可进入 Search Terms，不自动写入前台。';
    }
    return candidate.reason
      ? `${candidate.reason} 当前只保留作广告或边界参考，不写入 Listing。`
      : '该词只用于广告测试或需求边界判断，不写入 Listing。';
  }
  if (candidate.policy === 'must_use') {
    return candidate.reason
      ? `${candidate.reason} 但当前 Listing 尚未覆盖，需要重新诊断或人工确认。`
      : '策略要求使用该词，但当前 Listing 尚未覆盖，需要重新诊断或人工确认。';
  }
  if (candidate.selectedForSearchTerms) {
    if (candidate.selectionEvidence.has('step02')) {
      return 'Step02 已选中该词，但当前前台与 Search Terms 都未覆盖；可能在事实校验、前台去重或 250 字节打包时被移除。';
    }
    if (candidate.selectionEvidence.has('accepted_search_terms')) {
      return '生成阶段曾接纳该词，但当前成稿没有覆盖；可能被安全清理、去重或字节限制移除。';
    }
    return '该词已进入 Search Terms 候选，但当前成稿没有覆盖；请结合策略和字节预算判断是否补回。';
  }
  if (candidate.policy === 'backend_only') {
    return candidate.reason
      ? `${candidate.reason} 当前 Search Terms 未保留该词。`
      : '该词仅适合后台搜索词，但当前 Search Terms 未保留；可能已被前台覆盖、去重或字节限制移除。';
  }
  const planned = [...candidate.plannedFields]
    .map((field) => PLAN_FIELD_LABELS[field] || field)
    .filter(Boolean);
  if (planned.length) {
    return candidate.reason
      ? `${candidate.reason} 当前规划位置（${planned.join('、')}）没有实际写入。`
      : `生成规划曾考虑用于${planned.join('、')}，但当前成稿没有实际写入。`;
  }
  if (candidate.reason) return `${candidate.reason} 当前版本未采用。`;
  if (candidate.marketExpansion) {
    return '该词由市场扩展补入候选池，但当前版本未采用。';
  }
  return '该词有可追溯来源，但没有进入当前前台文案或 Search Terms。';
}

function sourceLabel(value) {
  const normalized = cleanText(value).toLocaleLowerCase();
  return SOURCE_LABELS[normalized] || cleanText(value);
}

function policyLabel(value) {
  return POLICY_LABELS[value] || value || POLICY_LABELS.unclassified;
}

function semanticRoleLabel(value) {
  return SEMANTIC_ROLE_LABELS[value] || value || '';
}

function marketDataStatus(metrics) {
  return [
    metrics.searches30d,
    metrics.purchaseRate,
    metrics.competition,
    metrics.cpc,
    metrics.supplyDemandRatio,
  ].some((value) => value !== null) ? 'available' : 'unavailable';
}

function firstPlacementOrder(row) {
  return Math.min(...row.placements.map((item) => item.order), 999);
}

function rowSort(a, b) {
  if (a.status !== b.status) return a.status === 'policy_conflict' ? -1 : 1;
  const orderDiff = firstPlacementOrder(a) - firstPlacementOrder(b);
  if (orderDiff) return orderDiff;
  const policyDiff = (POLICY_PRIORITY[b.policy] ?? -1) - (POLICY_PRIORITY[a.policy] ?? -1);
  if (policyDiff) return policyDiff;
  const relevanceDiff = (b.metrics.relevanceScore || 0) - (a.metrics.relevanceScore || 0);
  if (relevanceDiff) return relevanceDiff;
  const volumeDiff = (b.metrics.searches30d || 0) - (a.metrics.searches30d || 0);
  if (volumeDiff) return volumeDiff;
  return a.keyword.localeCompare(b.keyword);
}

function unusedRowSort(a, b) {
  const statusPriority = {
    missing_required: 5,
    selected_not_used: 4,
    planned_not_used: 3,
    excluded: 2,
    not_used: 1,
  };
  const statusDiff = (statusPriority[b.status] || 0) - (statusPriority[a.status] || 0);
  if (statusDiff) return statusDiff;
  const policyDiff = (POLICY_PRIORITY[b.policy] ?? -1) - (POLICY_PRIORITY[a.policy] ?? -1);
  if (policyDiff) return policyDiff;
  const relevanceDiff = (b.metrics.relevanceScore || 0) - (a.metrics.relevanceScore || 0);
  if (relevanceDiff) return relevanceDiff;
  const volumeDiff = (b.metrics.searches30d || 0) - (a.metrics.searches30d || 0);
  if (volumeDiff) return volumeDiff;
  return a.keyword.localeCompare(b.keyword);
}

function isRedundantSearchToken(row, rows) {
  if (row.keyword.includes(' ')) return false;
  if (row.metrics.dataStatus === 'available') return false;
  if (row.policy !== 'selected' && row.policy !== 'unclassified') return false;
  if (![...row.originKinds].every((kind) => ['searchTerms', 'searchTermsFallback'].includes(kind))) return false;
  return rows.some((other) => (
    other !== row
    && other.keyword.includes(' ')
    && containsExactKeyword(other.keyword, row.keyword)
    && other.placements.every((placement) => placement.field === 'searchTerms')
  ));
}

const TRACEABLE_SINGLE_TOKEN_ORIGINS = new Set([
  'selectedKeywords',
  'keywordAssetPackage',
  'keywordPlan',
  'generationLedger',
  'searchTermsAuthority',
  'searchTerms',
  'searchTermsFallback',
  'optimizerResult',
]);

function isTraceableUsageKeyword(row) {
  const tokens = meaningfulKeywordTokens(row.keyword);
  if (tokens.length === 0) return false;
  if (tokens.length > 1) return true;
  if (
    row.selectedForSearchTerms
    || row.policy === 'must_use'
    || row.policy === 'backend_only'
  ) {
    return true;
  }
  return asArray(row.originKinds).some((kind) => TRACEABLE_SINGLE_TOKEN_ORIGINS.has(kind));
}

const TRACEABLE_UNUSED_ORIGINS = new Set([
  'keywordAssetPackage',
  'keywordPlan',
  'keywordUsePolicy',
  'generationLedger',
  'searchTermsAuthority',
  'optimizerResult',
]);

function isTraceableUnusedKeyword(row) {
  const tokens = meaningfulKeywordTokens(row.keyword);
  if (!tokens.length) return false;
  if (
    row.selectedForSearchTerms
    || row.policy !== 'unclassified'
    || row.plannedFields.length > 0
  ) {
    return true;
  }
  return asArray(row.originKinds).some((kind) => TRACEABLE_UNUSED_ORIGINS.has(kind));
}

function equivalentSelectionRank(row) {
  const backendExact = row.placements.some((placement) => (
    placement.field === 'searchTerms' && placement.matchType === 'exact_phrase'
  ));
  const anyExact = row.placements.some((placement) => placement.matchType === 'exact_phrase');
  const evidence = new Set(asArray(row.selectionEvidence));
  return [
    backendExact ? 1 : 0,
    anyExact ? 1 : 0,
    evidence.has('current_state') || evidence.has('restored_search_terms') ? 1 : 0,
    evidence.has('accepted_search_terms') ? 1 : 0,
    evidence.has('step02') ? 1 : 0,
  ];
}

function compareEquivalentSelections(left, right) {
  const leftRank = equivalentSelectionRank(left);
  const rightRank = equivalentSelectionRank(right);
  for (let index = 0; index < leftRank.length; index += 1) {
    if (leftRank[index] !== rightRank[index]) return leftRank[index] - rightRank[index];
  }
  return 0;
}

function dedupeEquivalentSearchSelections(rows) {
  const winnerBySignature = new Map();
  for (const row of rows) {
    if (!row.selectedForSearchTerms) continue;
    const signature = keywordTokenSignature(row.keyword);
    if (!signature) continue;
    const current = winnerBySignature.get(signature);
    if (!current || compareEquivalentSelections(row, current) > 0) {
      winnerBySignature.set(signature, row);
    }
  }
  return rows.filter((row) => {
    if (!row.selectedForSearchTerms) return true;
    const signature = keywordTokenSignature(row.keyword);
    return !signature || winnerBySignature.get(signature) === row;
  });
}

export function buildKeywordUsageModel({
  listing = {},
  keywordLibrary = null,
  keywordUsePolicies = [],
  selectedKeywords = [],
  keywordPlan = null,
  generationLedger = null,
  keywordAssetPackages = [],
  listingDraftV1 = null,
  searchTermsSelectionLedger = null,
  optimizerResult = null,
} = {}) {
  const store = new Map();
  addKeywordLibrary(store, keywordLibrary);
  addAssetPackages(store, keywordAssetPackages);
  addPolicies(store, keywordUsePolicies);
  addKeywordPlan(store, keywordPlan);
  addGenerationLedger(store, generationLedger);
  for (const entry of asArray(selectedKeywords)) {
    mergeCandidate(store, entry, {
      originKind: 'selectedKeywords',
      plannedField: 'searchTerms',
      selectedForSearchTerms: true,
      backendAuthority: true,
      selectionEvidence: 'current_state',
      policy: 'selected',
      policyOriginRank: 1,
      source: 'current_selection',
    });
  }
  addSearchTermsCandidates(store, {
    listing,
    listingDraftV1,
    searchTermsSelectionLedger,
    selectedKeywords,
    keywordPlan,
    generationLedger,
  });
  addOptimizerResult(store, optimizerResult);
  const placementContext = buildPlacementContext(listing);

  let allRows = [...store.values()].map((candidate) => {
    const placements = buildPlacements(candidate, placementContext);
    const used = placements.length > 0;
    const conflict = used && hasPolicyConflict(candidate, placements);
    const status = used
      ? (conflict ? 'policy_conflict' : 'used')
      : unusedStatus(candidate);
    const metrics = {
      ...candidate.metrics,
      dataStatus: marketDataStatus(candidate.metrics),
    };
    return {
      keyword: candidate.keyword,
      normalizedKeyword: candidate.normalizedKeyword,
      policy: candidate.policy,
      policyLabel: policyLabel(candidate.policy),
      semanticRole: candidate.semanticRole,
      semanticRoleLabel: semanticRoleLabel(candidate.semanticRole),
      reason: used
        ? whyText(candidate, placements, conflict)
        : whyUnusedText(candidate),
      metrics,
      placements,
      sources: [...new Set(
        [...candidate.sources].map(sourceLabel).filter(Boolean),
      )].slice(0, 8),
      sourceAsins: [...candidate.sourceAsins].slice(0, 12),
      categories: [...candidate.categories]
        .map((item) => CATEGORY_LABELS[item] || item)
        .slice(0, 8),
      plannedFields: [...candidate.plannedFields]
        .map((item) => PLAN_FIELD_LABELS[item] || item)
        .slice(0, 8),
      selectedForSearchTerms: candidate.selectedForSearchTerms,
      selectionEvidence: [...candidate.selectionEvidence],
      status,
      statusLabel: used
        ? (conflict ? '策略冲突' : '已使用')
        : unusedStatusLabel(status),
      originKinds: [...candidate.originKinds],
    };
  });

  allRows = dedupeEquivalentSearchSelections(allRows);
  const rows = allRows
    .filter((row) => row.placements.length > 0)
    .filter(isTraceableUsageKeyword)
    .filter((row) => !isRedundantSearchToken(row, allRows))
    .sort(rowSort);
  const unusedRows = allRows
    .filter((row) => row.placements.length === 0)
    .filter(isTraceableUnusedKeyword)
    .filter((row) => !isRedundantSearchToken(row, allRows))
    .sort(unusedRowSort);
  const fieldCounts = {
    productTitle: 0,
    itemHighlights: 0,
    bullets: 0,
    description: 0,
    qa: 0,
    aplus: 0,
    searchTerms: 0,
  };
  for (const row of rows) {
    const fields = new Set(row.placements.map((item) => item.field));
    if (fields.has('productTitle')) fieldCounts.productTitle += 1;
    if (fields.has('itemHighlights')) fieldCounts.itemHighlights += 1;
    if (fields.has('bullet')) fieldCounts.bullets += 1;
    if (fields.has('description')) fieldCounts.description += 1;
    if (fields.has('qa')) fieldCounts.qa += 1;
    if (fields.has('aplus')) fieldCounts.aplus += 1;
    if (fields.has('searchTerms')) fieldCounts.searchTerms += 1;
  }

  return {
    schemaVersion: KEYWORD_USAGE_SCHEMA_VERSION,
    summary: {
      usedCount: rows.length,
      unusedCount: unusedRows.length,
      traceableCount: rows.length + unusedRows.length,
      visibleCount: rows.filter((row) => row.placements.some((item) => item.field !== 'searchTerms')).length,
      backendCount: rows.filter((row) => row.placements.some((item) => item.field === 'searchTerms')).length,
      backendOnlyCount: rows.filter((row) => row.placements.every((item) => item.field === 'searchTerms')).length,
      measuredCount: rows.filter((row) => row.metrics.dataStatus === 'available').length,
      missingMetricCount: rows.filter((row) => row.metrics.dataStatus === 'unavailable').length,
      unusedMeasuredCount: unusedRows.filter((row) => row.metrics.dataStatus === 'available').length,
      selectedButUnusedCount: unusedRows.filter((row) => row.status === 'selected_not_used').length,
      excludedCount: unusedRows.filter((row) => row.status === 'excluded').length,
      missingRequiredCount: unusedRows.filter((row) => row.status === 'missing_required').length,
      riskCount: rows.filter((row) => row.status === 'policy_conflict').length,
      fieldCounts,
    },
    rows,
    usedRows: rows,
    unusedRows,
  };
}
